/**
 * The back office's half of signing in.
 *
 * Small on purpose: a POST, a place to keep the token, and the discipline about
 * when to throw it away. Everything that decides anything lives on the server.
 *
 * WHERE THE TOKEN IS KEPT, and why it is the less obvious choice
 *
 * `sessionStorage`, not `localStorage`. A back office is opened on a laptop in
 * a restaurant office, frequently one that several people use, and
 * `localStorage` survives closing the tab — so the next person to open the
 * browser is signed in as the manager. `sessionStorage` is scoped to the tab,
 * which matches what a person means when they close the window.
 *
 * The cost is real and accepted: a reload keeps you signed in, a new tab does
 * not. For a screen somebody sits in front of for an hour at a time, that is
 * the right way round.
 *
 * Neither is proof against a script running on this origin. Nothing in a
 * browser is — an httpOnly cookie would resist reading but is sent
 * automatically, which trades this for CSRF. The token is short-lived and
 * revocable, which is the mitigation that actually holds.
 */

const STORAGE_KEY = "gf.console.session";

export interface SignedInStaff {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string;
}

export interface Session {
  readonly token: string;
  readonly expiresAt: string | null;
  readonly staff: SignedInStaff;
  readonly venueId: string;
  readonly scopes: readonly string[];
}

export type SignInFailure =
  | { readonly reason: "BAD_CREDENTIALS" }
  | { readonly reason: "NOT_BACK_OFFICE"; readonly roleId: string | null }
  | { readonly reason: "UNREACHABLE"; readonly detail: string }
  /** Too many wrong PINs: the server holds this name, or the venue, for a while. */
  | { readonly reason: "TOO_MANY_ATTEMPTS"; readonly retryAfterMinutes: number };

export type SignInResult =
  | { readonly ok: true; readonly session: Session }
  | ({ readonly ok: false } & SignInFailure);

export interface SignInArgs {
  readonly baseUrl: string;
  readonly venueId: string;
  readonly staffId: string;
  readonly pin: string;
  readonly fetch?: typeof fetch;
}

export async function signIn(args: SignInArgs): Promise<SignInResult> {
  const doFetch = args.fetch ?? fetch;

  let response: Response;
  try {
    response = await doFetch(`${args.baseUrl}/auth/session`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ venueId: args.venueId, staffId: args.staffId, pin: args.pin }),
    });
  } catch (cause) {
    /**
     * UNREACHABLE IS NOT A WRONG PIN.
     *
     * Told apart because they send somebody to completely different places: one
     * is "try again", the other is "the API is not running". A sign-in screen
     * that says "wrong PIN" when the server is down has people re-typing a
     * correct PIN for five minutes.
     */
    return { ok: false, reason: "UNREACHABLE", detail: (cause as Error).message };
  }

  if (response.status === 403) {
    // The one refusal the server is willing to explain, because the PIN was
    // right and the person has already proved who they are.
    const body = (await response.json().catch(() => ({}))) as { detail?: unknown };
    const roleId = typeof body.detail === "string" ? (/"([^"]+)"/.exec(body.detail)?.[1] ?? null) : null;
    return { ok: false, reason: "NOT_BACK_OFFICE", roleId };
  }

  if (response.status === 429) {
    const seconds = Number(response.headers.get("retry-after")) || 60;
    return { ok: false, reason: "TOO_MANY_ATTEMPTS", retryAfterMinutes: Math.ceil(seconds / 60) };
  }

  if (!response.ok) return { ok: false, reason: "BAD_CREDENTIALS" };

  const session = (await response.json()) as Session;
  save(session);
  return { ok: true, session };
}

/**
 * Sign out, and forget the token WHATEVER the server said.
 *
 * The order matters. If the request fails — the laptop is offline, the API is
 * restarting — the local token is still dropped, because a sign-out that leaves
 * somebody signed in because a network call failed is worse than a credential
 * that stays valid server-side until it expires. The button has to mean what it
 * says.
 */
export async function signOut(args: {
  baseUrl: string;
  session: Session;
  fetch?: typeof fetch;
}): Promise<void> {
  const doFetch = args.fetch ?? fetch;
  try {
    await doFetch(`${args.baseUrl}/v1/auth/session`, {
      method: "DELETE",
      headers: {
        authorization: `Bearer ${args.session.token}`,
        // Every mutation on this API carries one; this route is not special.
        "idempotency-key": crypto.randomUUID(),
      },
    });
  } catch {
    // Deliberately swallowed, and the only place in this app that is allowed
    // to: see above. `no-empty-catch` is satisfied by this comment existing.
  }
  forget();
}

export function save(session: Session): void {
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function forget(): void {
  window.sessionStorage.removeItem(STORAGE_KEY);
}

/**
 * The stored session, if there is one and it has not run out.
 *
 * EXPIRY IS CHECKED HERE TOO, which is not redundant with the server checking
 * it. Without this the console restores a dead token, renders its whole
 * dashboard, and every panel fails with a 401 — so the user sees a broken
 * product rather than a sign-in screen. The server remains the authority; this
 * is about which screen to draw.
 */
export function restore(now: Date = new Date()): Session | null {
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let session: Session;
  try {
    session = JSON.parse(raw) as Session;
  } catch {
    // Corrupt or from an older shape. Not an error worth showing anybody.
    forget();
    return null;
  }

  /**
   * `session` IS CHECKED FOR BEING AN OBJECT AT ALL.
   *
   * `JSON.parse("null")` does not throw — it succeeds and returns `null`, so
   * the catch above never runs and the next line reads `.token` off nothing.
   * The same goes for `"[]"` and `'"a string"'`. Storage is attacker-adjacent
   * on a shared machine and merely corrupt on an ordinary one; either way the
   * answer is "nobody is signed in", not a white screen.
   */
  if (typeof session !== "object" || session === null || Array.isArray(session)) {
    forget();
    return null;
  }

  if (typeof session.token !== "string" || !session.staff) {
    forget();
    return null;
  }

  if (session.expiresAt !== null && Date.parse(session.expiresAt) <= now.getTime()) {
    forget();
    return null;
  }

  return session;
}
